// Which calendars are switched on — the list Apple Calendar puts in its sidebar
// and Google puts under "Other calendars".
//
// It is mounted twice from one component, and that is the point: as a popover on
// the calendar screen, where the answer to "why is Diwali not showing" has to be
// one click away, and as a card on Profile, because the choice is saved to a
// person rather than to a workspace and Profile is where a person's settings
// live. Two mounts, one behaviour, one place to fix a bug.
//
// Everything here writes straight to `calendar_subscriptions`, whose row-level
// policy is `user_id = auth.uid()`. There is no save button because there is
// nothing to batch: ticking a box is the whole transaction.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  likelyCountries, refreshFeed, subscribeByUrl, subscribeToFeed, unsubscribeFromFeed,
  updateSubscription,
  type CalendarFeed, type CalendarSubscription, type EventCategory,
} from "../lib/calendar";
import { logError } from "../lib/logger";
import { Spinner } from "./Spinner";
import "./calendar-picker.css";

export interface CalendarChoice {
  feed: CalendarFeed;
  subscription: CalendarSubscription;
  /** The subscription's colour, or the feed's when it has no opinion. */
  hue: number;
}

/** Feeds this person subscribes to, with the colour to draw each one in. */
export function chooseVisible(
  feeds: CalendarFeed[],
  subscriptions: CalendarSubscription[],
): CalendarChoice[] {
  const byId = new Map(feeds.map((f) => [f.id, f]));
  return subscriptions
    .filter((s) => s.is_visible && byId.has(s.feed_id))
    .map((s) => {
      const feed = byId.get(s.feed_id)!;
      return { feed, subscription: s, hue: s.hue ?? feed.hue };
    });
}

/** A day older than this and the feed is worth apologising for. */
const STALE_AFTER_MS = 30 * 24 * 3600_000;

export function isStale(feed: CalendarFeed): boolean {
  if (!feed.ics_url) return false;
  if (feed.last_error) return true;
  if (!feed.last_fetched_at) return true;
  return Date.now() - new Date(feed.last_fetched_at).getTime() > STALE_AFTER_MS;
}

const CATEGORY_LABEL: Record<EventCategory, string> = {
  public: "Public holidays",
  observance: "Observances and marketing days",
  other: "Everything else",
};

export function CalendarPicker({
  feeds, subscriptions, onChanged, onClose,
}: {
  feeds: CalendarFeed[];
  subscriptions: CalendarSubscription[];
  /** Called after any change, so the calendar behind this can reload. */
  onChanged: () => Promise<void> | void;
  /** Present when this is a popover; absent on the Profile card. */
  onClose?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const subscribed = useMemo(
    () => new Map(subscriptions.map((s) => [s.feed_id, s])),
    [subscriptions],
  );

  const mine = useMemo(
    () => feeds.filter((f) => subscribed.has(f.id)),
    [feeds, subscribed],
  );

  const home = useMemo(() => new Set(likelyCountries()), []);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return feeds
      .filter((f) => !subscribed.has(f.id))
      .filter((f) => !q || f.name.toLowerCase().includes(q) ||
                     (f.country_code ?? "").toLowerCase().includes(q))
      // The country the browser thinks you are in, first — a guess offered,
      // never a subscription made on somebody's behalf.
      .sort((a, b) => Number(home.has(b.country_code ?? "")) - Number(home.has(a.country_code ?? "")));
  }, [feeds, subscribed, search, home]);

  /** Every mutation runs through here: one busy key, one error, one reload. */
  const run = useCallback(async (key: string, work: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await work();
      await onChanged();
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("calendar_view", e, { step: "change_subscription", context: { key } });
    } finally {
      setBusy(null);
    }
  }, [onChanged]);

  async function add(feed: CalendarFeed) {
    await run(`add:${feed.id}`, async () => {
      await subscribeToFeed(feed.id);
      // Fetch it now rather than at 3am: a calendar that stays empty until
      // tomorrow reads as broken, and the offline base only covers the
      // catalogue's countries.
      if (feed.ics_url) await refreshFeed(feed.id).catch(() => undefined);
    });
  }

  async function addByUrl(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    await run("add-url", async () => {
      const feed = await subscribeByUrl(url);
      setUrl("");
      setAdding(false);
      await refreshFeed(feed.id).catch(() => undefined);
    });
  }

  const body = (
    <>
      <div className="cal-picker-head">
        <h2>Calendars</h2>
        {onClose && (
          <button className="cal-picker-close" onClick={onClose} aria-label="Close">×</button>
        )}
      </div>

      <ul className="cal-picker-list">
        {/* Always on, never a checkbox: this is the screen's own content, and a
            calendar app that can hide your own events is a calendar app that
            gets a bug report saying everything disappeared. */}
        <li className="cal-picker-row fixed">
          <span className="cal-dot mine" aria-hidden="true" />
          <span className="cal-picker-name">My content</span>
          <span className="muted">always shown</span>
        </li>

        {mine.map((feed) => {
          const sub = subscribed.get(feed.id)!;
          const hue = sub.hue ?? feed.hue;
          return (
            <li key={feed.id} className="cal-picker-row">
              <label className="cal-picker-toggle">
                <input
                  type="checkbox"
                  checked={sub.is_visible}
                  disabled={busy !== null}
                  onChange={(e) =>
                    run(`vis:${feed.id}`, () =>
                      updateSubscription(feed.id, { is_visible: e.target.checked }))}
                />
                <span className="cal-dot swatch" style={{ "--swatch-h": hue } as React.CSSProperties}
                      aria-hidden="true" />
                <span className="cal-picker-name">{feed.name}</span>
              </label>

              {busy === `vis:${feed.id}` && <Spinner label="Saving" />}

              <button className="cal-picker-remove" disabled={busy !== null}
                      title="Remove this calendar"
                      onClick={() => run(`rm:${feed.id}`, () => unsubscribeFromFeed(feed.id))}>
                {busy === `rm:${feed.id}` ? <Spinner label="Removing" /> : "Remove"}
              </button>

              {sub.is_visible && (
                <div className="cal-picker-cats">
                  {(["public", "observance"] as EventCategory[]).map((cat) => (
                    <label key={cat} className="cal-picker-cat">
                      <input
                        type="checkbox"
                        checked={sub.categories.includes(cat)}
                        disabled={busy !== null}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...sub.categories, cat]
                            : sub.categories.filter((c) => c !== cat);
                          return run(`cat:${feed.id}`, () =>
                            updateSubscription(feed.id, { categories: next }));
                        }}
                      />
                      {CATEGORY_LABEL[cat]}
                    </label>
                  ))}
                </div>
              )}

              {/* A feed nobody can fetch must say so. Showing month-old dates
                  quietly is how a calendar loses the right to be trusted. */}
              {isStale(feed) && (
                <p className="cal-picker-stale">
                  {feed.last_error
                    ? `Last update failed: ${feed.last_error}`
                    : "Not updated yet."}
                  {feed.builtin_country
                    ? " Showing the offline copy — public holidays only."
                    : ""}
                  {" "}
                  <button className="linkish" disabled={busy !== null}
                          onClick={() => run(`refresh:${feed.id}`, () => refreshFeed(feed.id))}>
                    {busy === `refresh:${feed.id}` ? <Spinner label="Retrying" /> : "Try now"}
                  </button>
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <h3 className="cal-picker-sub">Add a calendar</h3>
      <input
        className="cal-picker-search"
        type="search"
        value={search}
        placeholder="Search countries…"
        aria-label="Search available calendars"
        onChange={(e) => setSearch(e.target.value)}
      />

      <ul className="cal-picker-list available">
        {available.slice(0, 8).map((feed) => (
          <li key={feed.id} className="cal-picker-row">
            <span className="cal-dot swatch" style={{ "--swatch-h": feed.hue } as React.CSSProperties}
                  aria-hidden="true" />
            <span className="cal-picker-name">{feed.name}</span>
            {home.has(feed.country_code ?? "") && <span className="chip">near you</span>}
            <button disabled={busy !== null} onClick={() => add(feed)}>
              {busy === `add:${feed.id}` ? <Spinner label="Adding" /> : "Add"}
            </button>
          </li>
        ))}
        {available.length === 0 && (
          <li className="muted cal-picker-empty">
            {search ? "No calendar matches that." : "Every calendar we offer is already on."}
          </li>
        )}
      </ul>

      {adding ? (
        <form className="cal-picker-url" onSubmit={addByUrl}>
          <label htmlFor="cal-url">Calendar address</label>
          <input
            id="cal-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… or webcal://…"
            autoFocus
          />
          <p className="muted">
            Any published calendar: a client's campaign dates, your team's shared calendar,
            a national-days feed. Look for “subscribe” or “ICS” wherever it is published.
          </p>
          <div className="row">
            <button className="primary" type="submit" disabled={busy !== null || !url.trim()}>
              {busy === "add-url" ? <><Spinner /> Adding…</> : "Add calendar"}
            </button>
            <button type="button" onClick={() => { setAdding(false); setUrl(""); }}>Cancel</button>
          </div>
        </form>
      ) : (
        <button className="linkish cal-picker-byurl" onClick={() => setAdding(true)}>
          Add by address (ICS or webcal)
        </button>
      )}

      {error && <div className="card err cal-picker-error">{error}</div>}

      {/* Required by the licence on the offline data, and the right place for
          it: beside the calendars it is the floor beneath. */}
      <p className="cal-picker-credit muted">
        Offline holiday dates from{" "}
        <a href="https://github.com/commenthol/date-holidays" target="_blank" rel="noreferrer noopener">
          date-holidays
        </a>{" "}
        (derived from Wikipedia,{" "}
        <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noreferrer noopener">
          CC BY-SA 3.0
        </a>
        ). Subscribed calendars are fetched from their publishers.
      </p>
    </>
  );

  if (!onClose) return <div className="card cal-picker inline">{body}</div>;

  return (
    <div className="cal-picker-backdrop" onClick={onClose}>
      <div className="cal-picker popover" role="dialog" aria-modal="true" aria-label="Calendars"
           onClick={(e) => e.stopPropagation()}>
        {body}
      </div>
    </div>
  );
}
